<template>
  <div class="flex flex-col place-content-center">
    <div class="flex flex-col gap-2">
      <h2 class="mb-6 max-w-xl text-5xl font-medium leading-[3.5rem] tracking-[-0.04rem] text-neutral-800">
        {{ t('auth.login.title') }}
      </h2>
      <h3 class="max-w-xl text-4xl font-medium leading-[3.5rem] tracking-[-0.04rem] text-neutral-800">
        {{ t('auth.login.title_sign') }}
      </h3>

      <p class="max-w-xl text-[1.125rem] font-normal leading-[1.4375rem] tracking-[-0.02rem] text-neutral-500">
        {{ t('auth.login.under_title') }}
      </p>
    </div>

    <form class="mt-12 flex flex-col place-content-center" @submit.prevent="onSubmit">
      <infuse-field :label="userNameType === UserNameType.EMAIL ? t('auth.common.email') : t('auth.common.username')">
        <infuse-input
          v-model="form.username"
          :type="userNameType === UserNameType.EMAIL ? 'email' : 'text'"
          placeholder="name@company.com"
          :tabindex="1"
          required
          autofocus
        />
      </infuse-field>

      <infuse-field :label="t('auth.common.password')">
        <template v-if="enableForgotPassword" #label>
          <router-link :to="{ name: 'auth-forgot-password' }">
            <label class="cursor-pointer text-xs font-medium leading-4 text-secondary-500">
              {{ t('auth.login.forgot_password') }}
            </label>
          </router-link>
        </template>

        <infuse-input
          v-model="form.password"
          type="password"
          :placeholder="t('auth.common.password')"
          :tabindex="2"
          required
        />
      </infuse-field>

      <div class="flex items-start">
        <div class="flex items-center gap-3">
          <infuse-check-box id="remember" v-model="rememberUsername" />

          <label for="remember" class="text-sm font-medium text-neutral-500"> {{ t('auth.login.remember_me') }} </label>
        </div>
      </div>

      <infuse-button size="large" class="mt-12" :loading="loading" :tabindex="3">
        {{ t('auth.common.login') }}
      </infuse-button>
    </form>

    <div v-if="showRegisterLink" class="mt-8 text-center">
      <router-link :to="{ name: 'auth-register' }" class="hover:underline">
        {{ t('auth.login.register_no_account_yet') }} <b class="font-bold">{{ t('auth.login.register') }}</b>
      </router-link>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { UserNameType } from '@infuse/auth/services';

import { InfuseButton, InfuseCheckBox, InfuseField, InfuseInput } from '@infuse/ui/components';
import { useAppSettingsStore } from '@infuse/utils';
import Cookies from 'js-cookie';
import { reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';

interface Props {
  loading?: boolean;
  showRegisterLink?: boolean;
  enableForgotPassword: boolean;
  userNameType: UserNameType;
}

interface Emits {
  (e: 'submit', form: { username: string; password: string }): void;
}

defineProps<Props>();
const emit = defineEmits<Emits>();

const { t } = useI18n();

const rememberUsername = ref<boolean>(!!Cookies.get('infuse_username'));

const form = reactive({
  username: Cookies.get('infuse_username') || '',
  password: ''
});

function onSubmit() {
  emit('submit', form);

  if (rememberUsername.value) {
    Cookies.set('infuse_username', form.username, {
      secure: !useAppSettingsStore().getAsBool('VITE_ALLOW_INSECURE_COOKIES'),
      expires: 365
    });
  }
}
</script>
